import { Badge, Box, Button, Center, HStack, Spinner, Stack, Text, useToast } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FiAward, FiExternalLink, FiFileText } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  ReceivedPublicBidDto,
  SourcingEventStatus,
  SourcingEventV2Dto,
  useConfirmAwardMutation,
  useGetEvaluationQuery,
  useListPublicBidsForEventQuery,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { useDisplayDate } from "../../../../common/formatting/useDisplayDate";
import { urls } from "../../../../urls";
import { AwardBidsTable } from "./AwardBidsTable";
import { AwardLetterWorkbench } from "./AwardLetterWorkbench";
import { CreateContractFromBidModal } from "./CreateContractFromBidModal";

const bidDocuments = (bid: ReceivedPublicBidDto) =>
  [
    ...(bid.documents ?? []),
    ...((bid.qualificationResponses ?? []).flatMap((response) => response.evidenceDocuments ?? [])),
  ].filter((document, index, documents) => documents.findIndex((d) => d.id === document.id) === index);

export const AwardOutcome = ({ event }: { event: SourcingEventV2Dto }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const apiError = useApiError();
  const navigate = useNavigate();
  const displayDate = useDisplayDate();
  const { data, isLoading: areBidsLoading } = useListPublicBidsForEventQuery({ eventId: event.id });
  const { data: evaluation, isError: isEvaluationError } = useGetEvaluationQuery({ eventId: event.id });
  const [confirm, { isLoading: isConfirming }] = useConfirmAwardMutation();
  const [contractModalBidId, setContractModalBidId] = useState<string | null>(null);

  const award = event.award;
  const bids = data?.bids ?? [];
  const winningBidIds = award?.winningBidIds ?? [];
  const winners = winningBidIds
    .map((id) => bids.find((b) => b.id === id))
    .filter((b): b is ReceivedPublicBidDto => !!b);
  const contractModalBid = winners.find((w) => w.id === contractModalBidId);
  const isDecision = event.status === SourcingEventStatus.AwardDecision;

  const standstillExpiresAt = award?.standstillExpiresAt ?? undefined;
  const standstillDispatched = standstillExpiresAt !== undefined;
  const standstillExpiry = standstillExpiresAt ? new Date(standstillExpiresAt) : undefined;

  // Re-render exactly when the standstill deadline passes so the Confirm button enables itself without
  // needing a manual refresh; Date.now() alone only re-evaluates on an unrelated render.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (standstillExpiry === undefined) return;
    const msUntilExpiry = standstillExpiry.getTime() - Date.now();
    if (msUntilExpiry <= 0) return;
    const timer = setTimeout(() => setNow(Date.now()), msUntilExpiry);
    return () => clearTimeout(timer);
  }, [standstillExpiry]);

  const standstillElapsed = standstillExpiry !== undefined && now >= standstillExpiry.getTime();
  const standstillDaysRemaining =
    standstillExpiry !== undefined
      ? Math.max(0, Math.ceil((standstillExpiry.getTime() - now) / (1000 * 60 * 60 * 24)))
      : 0;

  const onConfirm = async () => {
    try {
      await confirm({ eventId: event.id }).unwrap();
      toast({ title: t("Award confirmed"), status: "success", position: "top-right" });
    } catch (e) {
      apiError.trigger(e);
    }
  };

  return (
    <Stack spacing="6">
      <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p="6">
        <Text fontWeight="semibold" fontSize="sm" color="smMuted" textTransform="uppercase" mb="3">
          {t("Awarded suppliers")}
        </Text>
        <Stack spacing="2">
          {areBidsLoading ? (
            <Center py="2">
              <Spinner size="sm" color="smPrimary" />
            </Center>
          ) : winners.length > 0 ? (
            winners.map((winner) => (
              <HStack key={winner.id} spacing="3">
                <FiAward color="var(--chakra-colors-smPrimary)" />
                <Text fontWeight="semibold" fontSize="lg" color="smText">
                  {winner.ownerName || t("Unknown")}
                </Text>
                <Badge colorScheme="teal">{t("Winner")}</Badge>
              </HStack>
            ))
          ) : (
            <Text color="smMuted">{t("Unknown")}</Text>
          )}
        </Stack>
        {award?.internalNote && (
          <Box mt="4">
            <Text fontSize="xs" fontWeight="medium" color="smMuted" textTransform="uppercase">
              {t("Internal note")}
            </Text>
            <Text color="smText" whiteSpace="pre-wrap">
              {award.internalNote}
            </Text>
          </Box>
        )}
      </Box>

      {isEvaluationError ? (
        <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p="4">
          <Text fontSize="sm" color="smError">
            {t("Could not load the evaluation.")}
          </Text>
        </Box>
      ) : (
        evaluation &&
        evaluation.bids.length > 0 && (
          <AwardBidsTable evaluation={evaluation} mode="confirmed" selectedBidIds={winningBidIds} />
        )
      )}

      <AwardLetterWorkbench event={event} />

      {isDecision ? (
        <Box bg="smBackgroundSecondary" rounded="xl" border="1px solid" borderColor="smBorder" p="5">
          {!standstillDispatched ? (
            <Text fontSize="sm" color="smMuted" mb="3">
              {t("Send the award letters to start the standstill period before confirming the award.")}
            </Text>
          ) : standstillElapsed ? (
            <Text fontSize="sm" color="smMuted" mb="3">
              {t("The standstill period has passed. You can now confirm the award.")}
            </Text>
          ) : (
            <Text fontSize="sm" color="smMuted" mb="3">
              {t("Standstill period ends {{date}} ({{count}} days remaining). The award can be confirmed after this.", {
                date: standstillExpiresAt ? displayDate(standstillExpiresAt) : "",
                count: standstillDaysRemaining,
              })}
            </Text>
          )}
          <Button colorScheme="teal" onClick={onConfirm} isLoading={isConfirming} isDisabled={!standstillElapsed}>
            {t("Confirm award")}
          </Button>
        </Box>
      ) : (
        <Stack spacing="3">
          {winners.map((winner) => (
            <Box key={winner.id} bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p="5">
              <HStack justify="space-between">
                <HStack spacing="3">
                  <FiFileText />
                  <Text color="smText">{winner.ownerName || t("Unknown")}</Text>
                </HStack>
                {winner.contractId ? (
                  <Button
                    variant="outline"
                    colorScheme="teal"
                    rightIcon={<FiExternalLink />}
                    onClick={() => navigate(urls.contracts.edit.go(winner.contractId as string, event.id).details)}
                  >
                    {t("Go to contract")}
                  </Button>
                ) : (
                  <Button colorScheme="teal" onClick={() => setContractModalBidId(winner.id)}>
                    {t("Create contract")}
                  </Button>
                )}
              </HStack>
            </Box>
          ))}
        </Stack>
      )}

      {contractModalBid && (
        <CreateContractFromBidModal
          eventId={event.id}
          bidId={contractModalBid.id}
          supplierName={contractModalBid.ownerName}
          defaultTitle={event.eventData.title}
          documents={bidDocuments(contractModalBid)}
          isOpen={contractModalBidId !== null}
          onClose={() => setContractModalBidId(null)}
        />
      )}
    </Stack>
  );
};
